interface CartItem {
  variant_id: number;
  quantity: number;
  key: string;
}

interface CartState {
  items: CartItem[];
}

/**
 * Prevents the limited-drop product (free 4-pack) from being added to the cart
 * more than once. Detection is product-level: any variant of the product counts.
 *
 * Strategy (defence in depth):
 *  1. Disable the main Add-to-Cart button + dynamic checkout while it is in cart.
 *     The sticky ATC mirrors the main button automatically, so it disables too.
 *  2. Hard-block the add-to-cart form submit in the capture phase (covers the
 *     sticky button, fast double-clicks and any stale UI state).
 *  3. Re-check the cart on every cart mutation (Dawn, UpCart, ReCharge, …) by
 *     observing fetch() calls to /cart/* — no reload required.
 *  4. Safety net: if the cart somehow already holds quantity > 1, clamp it to 1.
 *
 * Config is read from data attributes on #NadStickyAddToCart (limited-drop only).
 */
export class NADLimitedDropGuard {
  private element: HTMLElement;
  private variantIds: Set<number>;
  private message: string;
  private inCartLabel: string;
  private blockDynamicCheckout: boolean;

  private inCart = false;
  private originalButtonHtml: string | null = null;
  private originalStickyText: string | null = null;
  private noticeEl: HTMLElement | null = null;
  private isClamping = false;

  private boundSubmit: (e: Event) => void;

  constructor(element: HTMLElement) {
    this.element = element;
    this.variantIds = new Set(
      (element.getAttribute('data-ld-variant-ids') || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
    this.message =
      element.getAttribute('data-ld-message') || 'This item is already in your cart.';
    this.inCartLabel = element.getAttribute('data-ld-label') || 'Already in your cart';
    this.blockDynamicCheckout = element.getAttribute('data-ld-block-checkout') !== 'false';

    this.boundSubmit = this.handleSubmit.bind(this);
    this.init();
  }

  private init(): void {
    if (this.variantIds.size === 0) return;

    // Hard block: capture phase runs before Dawn's <product-form> submit handler.
    document.addEventListener('submit', this.boundSubmit, true);

    this.watchCartMutations();
    this.bindReapplyTriggers();

    // Initial state.
    this.refresh();
  }

  /**
   * Block a duplicate add at submit time. Optimistically marks the product as in
   * cart on the first allowed add so a rapid second click is blocked before the
   * /cart.js round-trip resolves.
   */
  private handleSubmit(e: Event): void {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    const variantId = Number(new FormData(form).get('id'));
    if (!this.variantIds.has(variantId)) return;

    if (this.inCart) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.notify();
      this.apply();
      return;
    }

    // First add allowed — optimistically lock further adds.
    this.inCart = true;
    this.apply();
  }

  private async refresh(): Promise<void> {
    let cart: CartState;
    try {
      cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(
        (res) => res.json()
      );
    } catch {
      return;
    }

    const lines = (cart.items || []).filter((item) => this.variantIds.has(item.variant_id));
    this.inCart = lines.length > 0;

    // Safety net: never allow more than one of the limited-drop product.
    const overLine = lines.find((line) => line.quantity > 1);
    if (overLine && !this.isClamping) {
      await this.clampToOne(overLine.key);
      return;
    }

    this.apply();
  }

  private async clampToOne(key: string): Promise<void> {
    this.isClamping = true;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 1 }),
      });
      // Let other cart UIs (UpCart, header count) pick up the corrected quantity.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {
      // ignore — UI guard still prevents further adds
    } finally {
      this.isClamping = false;
    }
    this.apply();
  }

  /** Apply the in-cart / available state to the buy buttons. */
  private apply(): void {
    const mainSubmit = document.querySelector<HTMLButtonElement>(
      'product-form .product-form__submit'
    );
    const textWrapper = mainSubmit?.querySelector<HTMLElement>(
      '.product-form__submit-text-wrapper'
    );
    const stickySubmit = this.element.querySelector<HTMLButtonElement>(
      '[data-sticky-atc-submit]'
    );
    const stickyText = this.element.querySelector<HTMLElement>('[data-sticky-atc-button-text]');

    if (mainSubmit && textWrapper) {
      if (this.originalButtonHtml === null) {
        this.originalButtonHtml = textWrapper.innerHTML;
      }
      // Use aria-disabled + a class (not the native `disabled`) so we never fight
      // Dawn's own sold-out / availability handling. The submit interceptor is the
      // real guard; this just blocks the click and dims the button.
      if (this.inCart) {
        mainSubmit.setAttribute('aria-disabled', 'true');
        mainSubmit.classList.add('nad-ld-disabled');
        textWrapper.textContent = this.inCartLabel;
      } else {
        mainSubmit.removeAttribute('aria-disabled');
        mainSubmit.classList.remove('nad-ld-disabled');
        textWrapper.innerHTML = this.originalButtonHtml;
      }
    }

    // Mirror onto the sticky button immediately (its own observer may not fire on
    // cart changes). Cache the original label before the first overwrite so it can
    // be restored when the product is removed from the cart.
    if (stickySubmit && stickyText) {
      if (this.originalStickyText === null) {
        this.originalStickyText = stickyText.textContent;
      }
      stickySubmit.disabled = this.inCart;
      stickyText.textContent = this.inCart
        ? this.inCartLabel
        : this.originalStickyText || '';
    }

    if (this.blockDynamicCheckout) {
      const dynamic = document.querySelector<HTMLElement>(
        'product-form .shopify-payment-button'
      );
      if (dynamic) dynamic.style.display = this.inCart ? 'none' : '';
    }

    this.toggleNotice();
  }

  private toggleNotice(): void {
    const panel = document.querySelector<HTMLElement>(
      'product-form .product-form__buttons'
    );
    if (!panel) return;

    if (this.inCart) {
      if (!this.noticeEl) {
        this.noticeEl = document.createElement('p');
        this.noticeEl.className = 'nad-ld-guard__notice';
        this.noticeEl.setAttribute('role', 'status');
        this.noticeEl.textContent = this.message;
        panel.insertAdjacentElement('afterend', this.noticeEl);
      }
      this.noticeEl.hidden = false;
    } else if (this.noticeEl) {
      this.noticeEl.hidden = true;
    }
  }

  private notify(): void {
    const w = window as any;
    if (w.Noty) {
      new w.Noty({
        type: 'information',
        layout: 'topRight',
        theme: 'relax',
        text: this.message,
        timeout: 4000,
      }).show();
    }
  }

  /** Observe cart mutations across any app (Dawn, UpCart, ReCharge) via fetch. */
  private watchCartMutations(): void {
    const w = window as any;
    if (w.__nadLdFetchPatched) {
      document.addEventListener('nad:cart-mutated', () => this.scheduleRefresh());
      return;
    }
    w.__nadLdFetchPatched = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = (...fetchArgs: Parameters<typeof fetch>) => {
      const input = fetchArgs[0];
      const url = typeof input === 'string' ? input : (input as Request)?.url || '';
      const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      const promise = originalFetch(...fetchArgs);
      if (isCartMutation) {
        promise
          .then(() => document.dispatchEvent(new CustomEvent('nad:cart-mutated')))
          .catch(() => {});
      }
      return promise;
    };

    document.addEventListener('nad:cart-mutated', () => this.scheduleRefresh());
    window.addEventListener('pageshow', () => this.scheduleRefresh());
    window.addEventListener('focus', () => this.scheduleRefresh());
  }

  /** Re-apply when Dawn re-renders the buy buttons (variant change). */
  private bindReapplyTriggers(): void {
    document.addEventListener('product-info:loaded', () => setTimeout(() => this.apply(), 80));

    const variantInput = document.querySelector<HTMLInputElement>(
      'input[name="id"].product-variant-id'
    );
    variantInput?.addEventListener('change', () => setTimeout(() => this.apply(), 80));
  }

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleRefresh(): void {
    if (this.isClamping) return;
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refresh();
    }, 150);
  }

  public destroy(): void {
    document.removeEventListener('submit', this.boundSubmit, true);
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
  }

  static initializeAll(): void {
    document
      .querySelectorAll<HTMLElement>('#NadStickyAddToCart[data-ld-guard]')
      .forEach((el) => {
        if (!(el as any)._nadLimitedDropGuard) {
          (el as any)._nadLimitedDropGuard = new NADLimitedDropGuard(el);
        }
      });
  }
}
